import {
  referencesApi,
  type CreateReferencePayload,
  type UpdateReferencePayload,
} from "@api/references"
import { analytics } from "@lib/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { referenceKeys } from "./queryKeys"

import { useRealtimeEvent } from "@/realtime"
import type { ReferenceDocument } from "@/types"

const ONE_MB = 1024 * 1024
const FIVE_MB = 5 * ONE_MB

function bytesBucket(bytes: number): "lt_1mb" | "1_5mb" | "gt_5mb" {
  if (bytes < ONE_MB) return "lt_1mb"
  if (bytes <= FIVE_MB) return "1_5mb"
  return "gt_5mb"
}

/**
 * The professor's sources. Each row's progress bar binds to its own
 * TaskRun; when a `reference_ingest` run settles, the list refetches so
 * the row shows "Lista" (and its summary) or the failure code.
 */
export const useReferences = () => {
  const qc = useQueryClient()
  useRealtimeEvent("task.progress", (payload) => {
    if (payload.kind !== "reference_ingest") return
    if (payload.status === "completed" || payload.status === "failed") {
      void qc.invalidateQueries({ queryKey: referenceKeys.all })
    }
  })
  return useQuery({
    queryKey: referenceKeys.lists(),
    queryFn: referencesApi.list,
    placeholderData: (prev) => prev,
  })
}

export interface CreateReferenceVariables {
  payload: CreateReferencePayload
  onProgress?: (loaded: number, total: number) => void
}

export const useCreateReference = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payload, onProgress }: CreateReferenceVariables) =>
      referencesApi.create(payload, onProgress),
    onSuccess: (doc) => {
      analytics.track("reference_created", {
        kind: doc.kind,
        bytes_bucket: bytesBucket(doc.bytes),
        has_page_range: doc.page_from !== null,
        has_subject: doc.subject_id !== null,
      })
      void qc.invalidateQueries({ queryKey: referenceKeys.all })
    },
  })
}

export const useUpdateReference = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReferencePayload }) =>
      referencesApi.update(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: referenceKeys.all }),
  })
}

export const useRetryReference = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: referencesApi.retry,
    onSuccess: () => void qc.invalidateQueries({ queryKey: referenceKeys.all }),
  })
}

export const useDeleteReference = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (doc: ReferenceDocument) => referencesApi.remove(doc.id),
    onSuccess: (_result, doc) => {
      analytics.track("reference_deleted", {
        kind: doc.kind,
        used_in_materials: doc.used_in_materials,
      })
      void qc.invalidateQueries({ queryKey: referenceKeys.all })
    },
  })
}
